import {toBlobURL} from "@ffmpeg/util";

type FFmpegConfig = {
    coreURL: string,
    wasmURL: string,
    classWorkerURL: string
}

type LogCallback = (event: { message: string }) => void;
type ProgressCallback = (event: { progress: number; time: number }) => void;

const version = 8;

export default class FFmpegClient {
    public loaded = false;
    private worker: Worker | null = null;
    private resolves = new Map<number, { resolve: (data: any) => void, reject: (data: any) => void }>();
    private logCallbacks: LogCallback[] = [];
    private progressCallbacks: ProgressCallback[] = [];

    private static config: Promise<FFmpegConfig> | null = null;

    public constructor() {
    }

    private static async loadConfig(): Promise<FFmpegConfig> {
        const coreURL = await toBlobURL(`/ffmpeg/ffmpeg-core.js?v=${version}`, "text/javascript");
        const wasmURL = await toBlobURL(`/ffmpeg/ffmpeg-core.wasm?v=${version}`, "application/wasm");
        const classWorkerURL = await toBlobURL(`/ffmpeg/ffmpeg-worker.js?v=${version}`, "text/javascript");
        return {coreURL, wasmURL, classWorkerURL};
    }

    private static getConfig() {
        if (!FFmpegClient.config) {
            FFmpegClient.config = FFmpegClient.loadConfig();
        }
        return FFmpegClient.config;
    }

    public static async preload() {
        await FFmpegClient.getConfig();
    }

    /**
     * Loads ffmpeg-core inside web worker.
     */
    public async load(): Promise<boolean> {
        if (this.loaded) return true;

        const config = await FFmpegClient.getConfig();

        this.worker = new Worker(config.classWorkerURL);

        this.worker.onmessage = ({ data }: MessageEvent) => {
            if (!data) {
                return;
            }

            const { id, type, data: msgData } = data;

            if (type === 'LOG') {
                this.logCallbacks.forEach(cb => cb(msgData));
                return;
            }

            if (type === 'PROGRESS') {
                this.progressCallbacks.forEach(cb => cb(msgData));
                return;
            }

            const resolveInfo = this.resolves.get(id);
            if (resolveInfo) {
                this.resolves.delete(id);
                if (type === 'ERROR') {
                    resolveInfo.reject(new Error(msgData));
                } else {
                    resolveInfo.resolve(msgData);
                }
            }
        };

        await this.send('LOAD', {
            coreURL: config.coreURL,
            wasmURL: config.wasmURL,
            workerURL: config.classWorkerURL
        });

        this.loaded = true;
        return true;
    }

    /**
     * Generic function to send messages to web worker.
     */
    private send(type: string, data: any = {}, transfer: Transferable[] = [], signal?: AbortSignal): Promise<any> {
        if (!this.worker) throw new Error('Worker not initialized');

        return new Promise((resolve, reject) => {
            const id = Date.now() + Math.random();
            this.resolves.set(id, { resolve, reject });
            this.worker!.postMessage({ id, type, data }, transfer);

            signal?.addEventListener("abort", () => {
                this.resolves.delete(id);
                reject(new DOMException(`Message # ${id} was aborted`, "AbortError"));
            }, { once: true });
        });
    }

    public on(event: 'log' | 'progress', callback: LogCallback | ProgressCallback): void {
        if (event === 'log') {
            this.logCallbacks.push(callback as LogCallback);
        } else if (event === 'progress') {
            this.progressCallbacks.push(callback as ProgressCallback);
        }
    }

    public off(event: 'log' | 'progress', callback: LogCallback | ProgressCallback): void {
        if (event === 'log') {
            this.logCallbacks = this.logCallbacks.filter(cb => cb !== callback);
        } else if (event === 'progress') {
            this.progressCallbacks = this.progressCallbacks.filter(cb => cb !== callback);
        }
    }

    public terminate(): void {
        if (this.worker) {
            this.worker.terminate();
            this.worker = null;
        }
        this.loaded = false;
        this.resolves.clear();
    }

    // --- Core File System API ---

    public async exec(args: string[], timeout = -1, { signal }: { signal?: AbortSignal } = {}): Promise<any> {
        return this.send('EXEC', { args, timeout }, undefined, signal);
    }

    public async writeFile(path: string, data: Uint8Array | string, { signal }: { signal?: AbortSignal } = {}): Promise<boolean> {
        const trans: Transferable[] = [];
        let sendData = data;
        if (data instanceof Uint8Array) {
            sendData = data;
            // Optional: Add buffer to transfer list if you want to transfer ownership
            // trans.push(sendData.buffer);
        }
        return this.send('WRITE_FILE', { path, data: sendData }, trans, signal);
    }

    public async readFile(path: string, encoding = 'binary', { signal }: { signal?: AbortSignal } = {}): Promise<Uint8Array | string> {
        return this.send('READ_FILE', { path, encoding }, undefined, signal);
    }

    public async deleteFile(path: string, { signal }: { signal?: AbortSignal } = {}): Promise<boolean> {
        return this.send('DELETE_FILE', { path }, undefined, signal);
    }

    public async rename(oldPath: string, newPath: string, { signal }: { signal?: AbortSignal } = {}): Promise<boolean> {
        return this.send('RENAME', { oldPath, newPath }, undefined, signal);
    }

    public async createDir(path: string, { signal }: { signal?: AbortSignal } = {}): Promise<boolean> {
        return this.send('CREATE_DIR', { path }, undefined, signal);
    }

    public async deleteDir(path: string, { signal }: { signal?: AbortSignal } = {}): Promise<boolean> {
        return this.send('DELETE_DIR', { path }, undefined, signal);
    }

    public async listDir(path: string, { signal }: { signal?: AbortSignal } = {}): Promise<string[]> {
         return this.send('LIST_DIR', { path }, undefined, signal);
    }

    public async mount(fsType: string, options: any, mountPoint: string): Promise<boolean> {
        return this.send('MOUNT', { fsType, options, mountPoint });
    }

    public async unmount(mountPoint: string): Promise<boolean> {
        return this.send('UNMOUNT', { mountPoint });
    }

    /**
     * Mounts an OPFS file to the given virtual path in Emscripten.
     * The worker will obtain the file handle from the OPFS path.
     * @param opfsPath - The OPFS file path/name
     * @param virtualPath - The virtual path in Emscripten FS (e.g., '/dev/output.mp4')
     */
    public async mountOpfs(opfsPath: string, virtualPath: string): Promise<boolean> {
        return this.send('MOUNT_OPFS', { opfsPath, virtualPath });
    }

    /**
     * Closes an OPFS file handle in the worker.
     * Flushes writes and releases the SyncAccessHandle.
     * @param virtualPath - The virtual path in Emscripten FS (e.g., '/dev/output.mp4')
     */
    public async closeOpfs(virtualPath: string): Promise<boolean> {
        return this.send('CLOSE_OPFS', { virtualPath });
    }

    /**
     * Checks if an OPFS quota error occurred during the last OPFS write operation.
     * This flag is set when QuotaExceededError is caught in the OPFS device.
     * The flag is automatically reset after checking.
     */
    public async checkOpfsQuotaError(): Promise<boolean> {
        return this.send('CHECK_OPFS_QUOTA_ERROR', {});
    }
}
